import { XMarkIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useGetConversationTraces } from '@/hooks/useChat';
import { useWorkflowStatus } from '@/sections/insights/aiAssistant/hooks/useWorkflowStatus';
import { useTraceDrawerStore } from '@/sections/insights/aiAssistant/stores/traceDrawer.store';
import { TraceEvent } from '@/types';

import { TraceHierarchy } from './TraceHierarchy';
import { WorkflowSpinner } from './WorkflowSpinner';

const MIN_WIDTH = 280;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 320;
const STORAGE_KEY = 'ai-assistant-workflow-drawer-width';

interface TraceDrawerProps {
  /** Custom width for the drawer (default: '320px') - only used if no saved width */
  width?: string;
}

// Calculate workflow stats
function calculateWorkflowStats(traces: TraceEvent[]) {
  if (!traces || traces.length === 0) {
    return { totalDurationMs: 0, stepCount: 0 };
  }

  let earliest = Infinity;
  let latest = -Infinity;
  const spanIds = new Set<string>();

  traces.forEach(event => {
    if (!event) return;
    const cleanedTime = event.created?.includes('#')
      ? event.created.split('#').filter(Boolean).pop() || event.created
      : event.created;
    const normalizedTime = cleanedTime?.replace(/\.\d{4,}Z$/, match => {
      const ms = match.slice(1, 4);
      return `.${ms}Z`;
    });
    const time = new Date(normalizedTime || '').getTime();
    if (!isNaN(time)) {
      if (time < earliest) earliest = time;
      if (time > latest) latest = time;
    }
    if (event.span_id) {
      spanIds.add(event.span_id);
    }
  });

  if (earliest === Infinity || latest === -Infinity) {
    return { totalDurationMs: 0, stepCount: spanIds.size };
  }

  return {
    totalDurationMs: Math.max(0, latest - earliest),
    stepCount: spanIds.size,
  };
}

function formatTotalDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSec = seconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${remainingSec}s`;
  }
  return `${seconds}s`;
}

/**
 * TraceDrawer - Side drawer displaying workflow trace hierarchy.
 * Slides in from the right side of the conversation area.
 * Resizable by dragging the left edge.
 */
export const TraceDrawer: React.FC<TraceDrawerProps> = ({
  width: defaultWidth = '320px',
}) => {
  const { isOpen, traceId, close } = useTraceDrawerStore();
  const { data: traces = [] } = useGetConversationTraces(traceId);
  const typedTraces = traces as TraceEvent[];
  const { isRunning, isComplete } = useWorkflowStatus(typedTraces);

  // Initialize width from localStorage or default
  const [drawerWidth, setDrawerWidth] = useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= MIN_WIDTH && parsed <= MAX_WIDTH) {
        return parsed;
      }
    }
    const defaultParsed = parseInt(defaultWidth, 10);
    return !isNaN(defaultParsed) ? defaultParsed : DEFAULT_WIDTH;
  });

  const [isResizing, setIsResizing] = useState(false);

  const workflowStats = useMemo(
    () => calculateWorkflowStats(typedTraces),
    [typedTraces]
  );

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      const clampedWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, newWidth));
      setDrawerWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      localStorage.setItem(STORAGE_KEY, drawerWidth.toString());
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Prevent text selection while resizing
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing, drawerWidth]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ x: '100%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: '100%', opacity: 0 }}
          transition={{
            type: 'tween',
            duration: 0.25,
            ease: 'easeOut',
          }}
          className="bg-chariot-background-primary border-chariot-stroke absolute top-0 right-0 z-40 flex h-full flex-col border-l shadow-lg"
          style={{ width: `${drawerWidth}px` }}
        >
          {/* Resize handle */}
          <div
            onMouseDown={handleMouseDown}
            className="absolute top-0 left-0 h-full w-1 cursor-ew-resize transition-colors hover:bg-blue-500/50"
            title="Drag to resize"
          />
          {/* Header */}
          <div className="border-chariot-stroke flex items-center justify-between border-b px-4 py-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-chariot-text-primary text-sm font-medium">
                  Workflow
                </span>
                {isRunning && (
                  <div className="bg-chariot-stroke-info size-2 animate-pulse rounded-full" />
                )}
                {isComplete && (
                  <span className="text-xs text-green-500">Complete</span>
                )}
              </div>
              {workflowStats.stepCount > 0 && (
                <div className="text-chariot-text-tertiary text-[10px]">
                  {workflowStats.stepCount} step
                  {workflowStats.stepCount !== 1 ? 's' : ''} &middot;{' '}
                  {formatTotalDuration(workflowStats.totalDurationMs)} total
                </div>
              )}
            </div>
            <button
              onClick={close}
              className="text-chariot-text-secondary hover:text-chariot-text-primary rounded p-1 transition-colors"
              aria-label="Close workflow drawer"
            >
              <XMarkIcon className="size-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <TraceHierarchy traces={typedTraces} animateEntries />
          </div>

          {/* Footer - Spinner when running */}
          {isRunning && (
            <div className="border-chariot-stroke border-t">
              <WorkflowSpinner size="sm" />
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TraceDrawer;
